import { reflectKeys } from "@utils/key.reflactor.ts";

import { GradingRubric } from "@modules/grading-rubric/types.ts";
import { User } from "@modules/profile/profile-types.ts";
import { ComponentType, ResourceDomain } from "@/types/lesson.ts";

export const QUESTION_TYPE = reflectKeys([
  "MCQ",
  "MRQ",
  "MPQ",
  "REC",
  "FREE",
  "FILE",
  "FILL",
  "DND",
  "EEQ",
]);
export type QuestionType = (typeof QUESTION_TYPE)[keyof typeof QUESTION_TYPE];

export const QUESTION_WITH_DETAILS = ["REC", "FREE", "FILE", "DND"];
export const QUESTION_WITH_OPTIONS = ["MCQ", "MRQ", "FILL", "DND", "EEQ"];
export const QUESTION_WITH_STANDALONE_FEEDBACK = ["MCQ", "MRQ", "FILL", "DND", "EEQ"];

export const QUESTION_APP_USAGE = reflectKeys(["FEEDBACK", "TEST"]);
export type QuestionAppUsage = (typeof QUESTION_APP_USAGE)[keyof typeof QUESTION_APP_USAGE];

export const OPTION_DISPLAY_MODE = {
  MCQ: { value: "MCQ", label: "poll_type_mcq_label" },
  SLIDER: { value: "SLIDER", label: "poll_type_slider_label" },
  TEXT_INPUT: { value: "TEXTINPUT", label: "option_display_type_text_input" },
  SHARED_DROPDOWN: { value: "SHARED_DROPDOWN", label: "option_display_type_shared_dropdown" },
  DROPDOWN: { value: "DROPDOWN", label: "option_display_type_dropdown" },
};
export type OptionDisplayMode = keyof typeof OPTION_DISPLAY_MODE;

// BE's QuestionModel. differs from ActivityComponentQuestion
export interface Question {
  uuid: string;
  questionType: QuestionType;
  questionNo: number;
  ltiAppId?: number;
  questionBody: string;
  defaultAnswer: string;
  gradingRubric?: GradingRubric;
  gradingRubricUuid?: string;
  isNewRubric?: boolean; //exists only on FE
  questionOptionModels?: {
    indexNo: number;
  }[];
  children?: Question[];
  appSettings?: {
    appFeatureId: number;
    appUsage: QuestionAppUsage;
    indexNo?: number;
  }[];
  lfaAppId?: string;
  markAppId?: string;
  topParentQuestionUuid?: string;
  parentQuestionUuid?: string;
  showMarks: boolean;
  recommendedTime?: number;
  noOfAttempt?: number;
  marks: number;
  hasCorrectAnswer?: boolean;
  markByOption?: boolean;
  createdDate: string;
  modifiedDate: string;
  layout: QuestionLayout;
  status?: string;
  stimulus?: string;
}

export const QUESTION_LAYOUT = {
  TOP_DOWN: "TOP_DOWN",
  SIDE_BY_SIDE: "SIDE_BY_SIDE",
} as const;

export type QuestionLayout = (typeof QUESTION_LAYOUT)[keyof typeof QUESTION_LAYOUT];

export interface QuestionOption {
  id: number;
  fileName: string | null;
  answer: string;
  rt_answer?: string;
  feedback: string;
  answerOption: boolean;
  isExisting?: boolean;
  actionStatus?: string;
  caseSensitive: boolean;
  indexNo: number;
  optionGroupNo?: number | null;
  wrongAnswer: string | null;
  wrongAnswerFeedback: string | null;
  mark: number;
}
export interface OptionOrientation {
  xCoordinate: string;
  yCoordinate: string;
}

// serialized by BE from Question
export interface ActivityComponentQuestion {
  id: string | number;
  uuid: string;
  questionBody: string;
  ltiAppId?: number;
  recommendedTime: number;
  questionType: string;
  questionOptions: QuestionOption[];
  optionDisplayMode: string;
  marks: number;
  // numOfHint?: number;
  allContainError: boolean;
  showSelectedError: boolean;
  hasCorrectAnswer?: boolean;
  speechEvaluation?: string;
  hint: string;
  optionOrientation: string;
  fileName: string;
  gradingRubric?: GradingRubric;
  showMarks?: boolean;
  stimulus?: string;
}

export interface ActivityComponentQuestionHint {
  id: number;
  indexNo: number;
  description: string;
  rt_description: string;
}

export interface ActivityComponentQuestionDisplayConfig {
  questionBodyPlaceholder?: string;
  showAnswerArea?: boolean;
  showMarkInput?: boolean;
  showAlphabeticalOption?: boolean;
  showTeacherFeedback?: boolean;
  stimulusPlaceholder?: string;
}

export interface Comment {
  academicYear: number;
  avatarFilename: string;
  comment: string;
  commentedBy: string;
  commentedByTeacher: boolean;
  commentedByUserId: number;
  createdDate: string;
  deleted: boolean;
  id: number;
  interactionCount: number;
  modifiedBy: string;
  modifiedByUserId: number;
  modifiedDate: string;
  notify: boolean;
  resourceId: number;
  resourceType: string;
  resourceUuid: string;
  teamId: number;
  uuid: string;
  appFeatureId: number;
}

export interface Poll {
  id: number;
  uuid: string;
  title: string;
  keywords?: string;
  createdById: number;
  createdByName: string;
  createdDateString: string; //datestring formatted by BE
  modifiedById: number;
  modifiedByName: string;
  modifiedDateString: string; //datestring formatted by BE
  owner: User;
  status: string;
  pollBody: string;
  multipleAnswers: boolean;
  pollOptions: {
    id: number;
    answer: string; //html
  };
  sourceUuid: string;
  resourceDomain: ResourceDomain;
  optionDisplayMode: OptionDisplayMode;
  hideResult: boolean;
  searchable: boolean;
  allTeamResponseViewable: boolean;
  resourceSharingComponentType: ComponentType;
  ccpmResource: boolean;
  resourceType: ComponentType;
  notifyStudents: boolean;
  resourceId: number;
  resourceUuid: string;
  number: number;
}

export interface Topic {
  uuid: string;
  number: number;
}
